import { Factory, Wheat, Snowflake, Package, Settings, Truck } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import MarqueeStrip from "@/components/MarqueeStrip";
import { FadeUp } from "@/components/Reveal";
import { IMAGES, CERTIFICATIONS } from "@/data/content";
import SEO from "@/components/SEO";

const CAPABILITIES = [
  { icon: Factory, anim: "animate-pulse-soft", value: "36", label: "Production Centers" },
  { icon: Wheat, anim: "animate-sway", value: "8,000+", label: "Acres Under Cultivation" },
  { icon: Snowflake, anim: "animate-gear", value: "10,00,000 ft³", label: "Cold Storage Capacity" },
  { icon: Package, anim: "animate-bob", value: "120,000+ sq ft", label: "Warehousing" },
];

const PROCESSING_STEPS = ["Purification & conditioning", "Precision grading", "Enhancement treatments", "Quality assessment"];

const DISTRIBUTION_POINTS = ["10,00,000 cubic feet cold storage", "120,000+ sq ft warehousing", "1200+ dealer network", "Timely delivery across India"];

const STAGES = [
  { title: "Breeder & Foundation Seed", text: "Nucleus seed from R&D farms is multiplied under breeder supervision with strict isolation distances and roguing protocols." },
  { title: "Certified Seed Production", text: "A trained grower network across diverse seed belts produces commercial lots under the Indian Minimum Seed Certification Standards." },
  { title: "Processing & Conditioning", text: "State-of-the-art processing units ensure every seed meets stringent quality standards through advanced technology." },
  { title: "Quality Testing & Release", text: "Physical purity, germination, vigour and genetic purity are verified in ISTA-aligned labs before any lot is released." },
];

const Production = () => (
  <>
    <SEO
      title="Production & Processing"
      description="36 production centers, 8,000+ acres under cultivation, 10,00,000 cu ft cold storage and certified processing — 95%+ germination standards from Seed Park India."
    />
    <PageHero
      eyebrow="Production & Processing"
      titleLines={["Scale that delivers.", "Quality you can verify."]}
      description="36 production centers, 8,000+ acres under cultivation, 10,00,000 cubic feet of cold storage and a 1200+ dealer network — built for reliable seed supply and timely delivery across India."
      image={IMAGES.aerial}
    />

    <section data-testid="production-capabilities" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Production Capabilities"
          titleLines={["Infrastructure built", "for a nation's appetite."]}
          description="From seed multiplication farms to climate-controlled storage, Seed Park operates one of the most capable hybrid seed supply chains in the region."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <FadeUp key={c.label} delay={i * 0.08} className="h-full">
                <div className="h-full border border-[#123C6B]/10 bg-forest-900 p-8" data-testid={`capability-${i}`}>
                  <Icon className={`h-7 w-7 text-corp-500 ${c.anim}`} />
                  <p className="mt-5 font-display text-3xl font-black tracking-tight text-brand-400">{c.value}</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">{c.label}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>

    <section data-testid="production-stages" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="The Production Chain"
          titleLines={["Four stages.", "One unbroken chain", "of custody."]}
        />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((s, i) => (
            <FadeUp key={s.title} delay={i * 0.08} className="h-full">
              <div className="group h-full border border-[#123C6B]/10 bg-white p-8 transition-colors hover:border-brand-400/50" data-testid={`production-stage-${i}`}>
                <p className="font-display text-4xl font-black text-outline group-hover:text-brand-400 group-hover:[-webkit-text-stroke:0]">0{i + 1}</p>
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <MarqueeStrip />

    <section data-testid="processing-grading" className="py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="Processing & Grading"
            titleLines={["Every seed, precision-", "graded and enhanced."]}
            description="State-of-the-art processing units ensure every seed meets stringent quality standards through advanced technology."
          />
          <div className="flex flex-wrap gap-3" data-testid="processing-chips">
            {PROCESSING_STEPS.map((s) => (
              <span key={s} className="border border-[#123C6B]/15 bg-forest-900 px-5 py-3 text-sm font-semibold text-foreground">
                {s}
              </span>
            ))}
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-14">
              <h3 className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-corp-500">
                <Truck className="h-4 w-4 animate-truck" /> Storage & Distribution
              </h3>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-mist">
                10,00,000 cubic feet of cold storage, 120,000+ sq ft of warehousing and a 1200+ dealer network
                keep seed viable and available — delivered on time, every season.
              </p>
              <div className="mt-6 flex flex-wrap gap-3" data-testid="distribution-chips">
                {DISTRIBUTION_POINTS.map((s) => (
                  <span key={s} className="border border-brand-400/40 bg-brand-400/5 px-5 py-3 text-sm font-semibold text-foreground">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <FadeUp delay={0.1}>
            <div className="relative overflow-hidden border border-[#123C6B]/10">
              <img src={IMAGES.lab2} alt="Seed processing and quality laboratory" loading="lazy" className="aspect-[3/4] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-corp-700/70 to-transparent" />
              <p className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white">
                <Settings className="h-3.5 w-3.5 animate-gear" /> Processing & Quality Control
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <section data-testid="quality-lab-section" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Quality Control"
          titleLines={["If it doesn't pass,", "it doesn't ship."]}
          description="Every commercial lot is sampled and tested against internal benchmarks that exceed certification minimums."
        />
        <div className="grid gap-px border border-[#123C6B]/10 bg-[#123C6B]/10 sm:grid-cols-3">
          {[
            ["99%", "Physical purity benchmark across lots"],
            ["95%+", "Germination standard before release"],
            ["100%", "Lots tested for genetic purity"],
          ].map(([v, l], i) => (
            <FadeUp key={l} delay={i * 0.08}>
              <div className="bg-white p-8">
                <p className="font-display text-4xl font-black text-corp-500">{v}</p>
                <p className="mt-2 text-sm leading-relaxed text-mist">{l}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="certifications-section" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Certifications & Standards"
          titleLines={["Credentials that", "stand up to audit."]}
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CERTIFICATIONS.map((c, i) => (
            <FadeUp key={c.title} delay={i * 0.08} className="h-full">
              <div className="h-full border border-[#123C6B]/10 bg-forest-900 p-8 transition-colors hover:border-gold-400/50" data-testid={`certification-${i}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-400">Standard</p>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{c.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Production;
